import { useEffect, useMemo, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import type { SearchSort } from '@/api'
import { queryKeys } from '@/lib/queryKeys'
import { ProductCard, ProductCardSkeleton } from '@/components/product/ProductCard'
import { SelectField } from '@/components/ui/Field'
import { Spinner } from '@/components/ui/Spinner'

const SORT_LABELS: Record<SearchSort, string> = {
  relevance: 'Mais relevantes',
  price_asc: 'Menor preço',
  price_desc: 'Maior preço',
  discount: 'Maior desconto',
}

/**
 * Resultados de busca e listagem por categoria (a mesma tela serve às duas
 * rotas). Carrega mais itens conforme o usuário chega ao fim da lista.
 */
export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { slug: categorySlug } = useParams<{ slug?: string }>()

  const term = searchParams.get('q') ?? ''
  const sort = (searchParams.get('ordenar') as SearchSort | null) ?? 'relevance'

  const { data: categories } = useQuery({
    queryKey: queryKeys.categories,
    queryFn: api.catalog.getCategories,
  })

  const category = useMemo(
    () => categories?.find((c) => c.slug === categorySlug) ?? null,
    [categories, categorySlug],
  )

  // Espera as categorias carregarem antes de buscar por slug, senão a primeira
  // requisição sairia sem o filtro e mostraria o catálogo inteiro.
  const ready = !categorySlug || !!category

  const searchKey = { q: term, categoryId: category?.id, sort }
  const query = useInfiniteQuery({
    queryKey: queryKeys.search(searchKey),
    queryFn: ({ pageParam }) =>
      api.catalog.searchProducts({ ...searchKey, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: ready,
  })

  const items = query.data?.pages.flatMap((page) => page.items) ?? []
  const total = query.data?.pages[0]?.total
  const sentinelRef = useRef<HTMLDivElement>(null)

  // Scroll infinito: dispara a próxima página quando o rodapé da lista aparece.
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !query.hasNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !query.isFetchingNextPage) {
          query.fetchNextPage()
        }
      },
      { rootMargin: '400px' },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [query])

  const heading = category
    ? category.name
    : term
      ? `Resultados para "${term}"`
      : 'Todos os produtos'

  function handleSortChange(value: string) {
    const next = new URLSearchParams(searchParams)
    if (value === 'relevance') next.delete('ordenar')
    else next.set('ordenar', value)
    setSearchParams(next, { replace: true })
  }

  const showEmpty = !query.isLoading && ready && items.length === 0

  return (
    <div className="mx-auto max-w-[1200px] px-4 py-5">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[17px] font-semibold text-ink lg:text-xl">{heading}</h1>
          {total != null && (
            <p className="mt-0.5 text-[13px] text-ink-soft">
              {total} {total === 1 ? 'produto encontrado' : 'produtos encontrados'}
            </p>
          )}
        </div>

        <SelectField
          label="Ordenar por"
          value={sort}
          onChange={(event) => handleSortChange(event.target.value)}
          className="w-52"
        >
          {Object.entries(SORT_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </SelectField>
      </div>

      {showEmpty ? (
        <div className="rounded-card bg-surface px-6 py-16 text-center">
          <p className="text-lg font-semibold text-ink">Nenhum produto encontrado</p>
          <p className="mt-1.5 text-sm text-ink-soft">
            Tente outras palavras ou revise a escrita do termo buscado.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {query.isLoading || !ready
            ? Array.from({ length: 8 }).map((_, index) => (
                <ProductCardSkeleton key={index} variant="grid" />
              ))
            : items.map((product) => (
                <ProductCard key={product.id} product={product} variant="grid" />
              ))}
        </div>
      )}

      <div ref={sentinelRef} className="flex h-16 items-center justify-center">
        {query.isFetchingNextPage && <Spinner size={26} className="text-brand" />}
      </div>
    </div>
  )
}
